/**
 * The 2026 settings-surface vocabulary: bordered panel, panel header, and the
 * text ramp used inside it.
 *
 * Org Settings established this look; it lives here rather than under
 * `Pages/OrgSettings` so Partials can share it without importing from a Page,
 * which the component hierarchy forbids.
 */
import { css } from '@emotion/react';

import { KAI_SHIKI_ORG_SETTINGS_COLORS } from '@/Client/Styles/kaiShikiTokens';
import { pxToRem } from '@/Client/Styles/pxToRem';
import { withOpacity } from '@kaiizen/kai-shiki/core';

export const SETTINGS_COLORS = KAI_SHIKI_ORG_SETTINGS_COLORS;

/** Modal backdrop, derived from the ink token rather than a hardcoded rgba(). */
export const SETTINGS_SCRIM_COLOR = withOpacity(SETTINGS_COLORS.text, 0.5);

export const SETTINGS_SPACING = {
  gutterX: 20,
  standardPadding: 24,
  sectionInsetTop: 21,
} as const;

export const SETTINGS_SECTION_INSET_STYLE = css`
  label: SETTINGS_SECTION_INSET_STYLE;
  width: 100%;
  padding: ${SETTINGS_SPACING.sectionInsetTop}px ${SETTINGS_SPACING.gutterX}px 0;
`;

export const SETTINGS_PANEL_STYLE = css`
  label: SETTINGS_PANEL_STYLE;
  width: 100%;
  border: 1px solid ${SETTINGS_COLORS.border};
  border-radius: 3px;
  background: ${SETTINGS_COLORS.white};
  overflow: hidden;
`;

export const SETTINGS_PANEL_HEADER_STYLE = css`
  label: SETTINGS_PANEL_HEADER_STYLE;
  min-height: 44px;
  padding: 10px ${SETTINGS_SPACING.gutterX}px;
  display: flex;
  align-items: center;
  background: ${SETTINGS_COLORS.panelHeaderBackground};
  border-bottom: 1px solid ${SETTINGS_COLORS.border};
  font-family: CircularXX, sans-serif;
  font-size: ${pxToRem(16)};
  font-style: normal;
  font-weight: 400;
  line-height: 1.25;
  color: ${SETTINGS_COLORS.text};
`;

export const SETTINGS_SUBHEADING_STYLE = css`
  label: SETTINGS_SUBHEADING_STYLE;
  font-family: CircularXX, sans-serif;
  font-size: ${pxToRem(18)};
  font-style: normal;
  font-weight: 700;
  line-height: 1.333;
  letter-spacing: 0.18px;
  color: ${SETTINGS_COLORS.text};
`;

export const SETTINGS_BODY_TEXT_STYLE = css`
  label: SETTINGS_BODY_TEXT_STYLE;
  font-family: CircularXX, sans-serif;
  font-size: ${pxToRem(12)};
  font-style: normal;
  font-weight: 400;
  line-height: 1.333;
  letter-spacing: 0.24px;
  color: ${SETTINGS_COLORS.textMuted};
`;

export const SETTINGS_BODY_TEXT_LIGHT_STYLE = css`
  label: SETTINGS_BODY_TEXT_LIGHT_STYLE;
  font-family: CircularXX, sans-serif;
  font-size: ${pxToRem(12)};
  font-style: normal;
  font-weight: 400;
  line-height: 1.333;
  letter-spacing: 0.24px;
  color: ${SETTINGS_COLORS.textMutedLight};
`;

export const SETTINGS_CAPTION_STYLE = css`
  label: SETTINGS_CAPTION_STYLE;
  font-family: CircularXX, sans-serif;
  font-size: ${pxToRem(10)};
  font-style: normal;
  font-weight: 400;
  line-height: 1.6;
  letter-spacing: 0.2px;
`;

export const SETTINGS_TITLE_REGULAR_STYLE = css`
  label: SETTINGS_TITLE_REGULAR_STYLE;
  font-family: CircularXX, sans-serif;
  font-size: ${pxToRem(20)};
  font-style: normal;
  font-weight: 400;
  line-height: 1.2;
  letter-spacing: 0.4px;
  color: ${SETTINGS_COLORS.text};
`;

/** The 2026 pill toggle, as Org Settings renders it. Pair with `CommonToggle`. */
export const SETTINGS_TOGGLE_TRACK_STYLE = {
  width: 53,
  height: 24,
  border: 0,
  borderRadius: 999,
  background: SETTINGS_COLORS.green,
  padding: 0,
  position: 'relative',
  cursor: 'pointer',
} as const;

export const SETTINGS_TOGGLE_TRACK_OFF_STYLE = {
  background: SETTINGS_COLORS.border,
} as const;

export const SETTINGS_TOGGLE_KNOB_STYLE = {
  position: 'absolute',
  top: 3,
  left: 31,
  width: 18,
  height: 18,
  borderRadius: 999,
  background: SETTINGS_COLORS.white,
} as const;

export const SETTINGS_TOGGLE_KNOB_OFF_STYLE = {
  left: 4,
} as const;
